import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton as MuiIconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIcon from '@mui/icons-material/Phone';
import { AppColors } from '../theme/colors';
import { type IndustryType, INDUSTRY_TYPE_LABELS } from '../constants/industryTemplates';
import {
  ADMIN_PASSWORD_RULE_ERROR,
  ADMIN_PASSWORD_RULE_LABEL,
  isValidAdminPassword,
} from '../utils/adminPassword';

type FieldKey =
  | 'companyName'
  | 'corporateCode'
  | 'contactPerson'
  | 'contactEmail'
  | 'phoneNumber'
  | 'adminPassword'
  | 'adminPasswordConfirm';
type FieldErrors = Partial<Record<FieldKey, string>>;

/** ボタンの下に出す案内。押しても何も変わらないように見えないよう、ここにも出す */
const FIELD_ERROR_SUMMARY = '入力内容に誤りがあります。赤字の項目をご確認ください。';

/**
 * サーバーが返したメッセージを、該当する入力欄に振り分ける。
 * 当てはまらないものは undefined（ボタンの下に出す）
 */
function fieldOfServerError(message: string): FieldKey | undefined {
  if (message.includes('会社ID')) return 'corporateCode';
  if (message.includes('管理者パスワード')) return 'adminPassword';
  return undefined;
}

interface FormData {
  companyName: string;
  corporateCode: string;
  contactPerson: string;
  department: string;
  contactEmail: string;
  phoneNumber: string;
  adminPassword: string;
  adminPasswordConfirm: string;
  industryType: IndustryType;
}

const FIELD_KEYS: FieldKey[] = [
  'companyName',
  'corporateCode',
  'contactPerson',
  'contactEmail',
  'phoneNumber',
  'adminPassword',
  'adminPasswordConfirm',
];

function isFieldKey(field: keyof FormData): field is FieldKey {
  return (FIELD_KEYS as string[]).includes(field);
}

/**
 * 1項目分の入力チェック。問題があればメッセージ、なければ undefined。
 * 欄を離れたとき・書き直したとき・登録ボタンを押したときに同じものを使う
 */
function validateField(field: FieldKey, data: FormData): string | undefined {
  switch (field) {
    case 'companyName':
      return data.companyName.trim() ? undefined : '会社名を入力してください';
    case 'corporateCode': {
      // 英数字と - _ のみ、3〜32文字
      const code = data.corporateCode.trim();
      if (!code) return '会社IDを入力してください';
      return /^[A-Za-z0-9_-]{3,32}$/.test(code)
        ? undefined
        : '会社IDは英数字と - _ のみで3〜32文字で入力してください';
    }
    case 'contactPerson':
      return data.contactPerson.trim() ? undefined : '担当者名を入力してください';
    case 'contactEmail': {
      const email = data.contactEmail.trim();
      if (!email) return '連絡先メールアドレスを入力してください';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ? undefined
        : 'メールアドレスの形式で入力してください';
    }
    case 'phoneNumber': {
      // 任意。入っているときだけ形式を見る
      const phone = data.phoneNumber.trim();
      return !phone || /^[0-9\-\(\)]+$/.test(phone)
        ? undefined
        : '電話番号は数字、ハイフン、括弧のみで入力してください';
    }
    case 'adminPassword':
      // 条件を満たさないものはサーバーでも弾かれるが、送信前に止めて理由をその場で伝える
      if (!data.adminPassword) return '管理者パスワードを入力してください';
      return isValidAdminPassword(data.adminPassword) ? undefined : ADMIN_PASSWORD_RULE_ERROR;
    case 'adminPasswordConfirm':
      if (!data.adminPasswordConfirm) return '確認のため、もう一度入力してください';
      return data.adminPassword === data.adminPasswordConfirm ? undefined : 'パスワードが一致しません';
  }
}

/** 指定した項目のエラーを、いまの入力内容で調べ直した結果に置き換える */
function revalidate(prev: FieldErrors, fields: FieldKey[], data: FormData): FieldErrors {
  const next = { ...prev };
  for (const field of fields) {
    const message = validateField(field, data);
    if (message) {
      next[field] = message;
    } else {
      delete next[field];
    }
  }
  return next;
}

export const CompanyRegistrationPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    corporateCode: '',
    contactPerson: '',
    department: '',
    contactEmail: '',
    phoneNumber: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    industryType: 'default',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompanyIdError, setIsCompanyIdError] = useState(false);
  // 項目ごとのエラー。問題のある入力欄のすぐ下に出す
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // 赤字の項目をすべて直したら、ボタンの下の案内も消す
  useEffect(() => {
    if (error === FIELD_ERROR_SUMMARY && Object.keys(fieldErrors).length === 0) {
      setError(null);
    }
  }, [error, fieldErrors]);

  // 初期表示では会社IDは空のまま（自動生成しない）

  const generateCorporateCode = async () => {
    try {
      const { collection, query, where, limit, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const companiesRef = collection(db, 'companies');
      
      // 英数字ランダム生成（8文字）
      const generateRandomCode = (): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
          code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
      };
      
      // 重複チェック（最大10回まで）
      let corporateCode = '';
      let retryCount = 0;
      const maxRetries = 10;
      
      while (retryCount < maxRetries) {
        corporateCode = generateRandomCode();
        
        // 重複チェック
        const q = query(
          companiesRef,
          where('corporateCode', '==', corporateCode),
          limit(1)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          // 重複なし、ループ終了
          break;
        }
        
        retryCount++;
      }
      
      if (retryCount >= maxRetries) {
        throw new Error('会社IDの生成に失敗しました。ネットワーク接続を確認してから再度お試しください');
      }
      
      setFormData(prev => ({ ...prev, corporateCode }));
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next.corporateCode;
        return next;
      });
    } catch (error) {
      console.error('会社ID生成エラー:', error);
      setError('会社IDの生成に失敗しました');
      setIsCompanyIdError(true);
    }
  };


  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const nextData = { ...formData, [field]: value };

    setFormData(nextData);
    if (!isFieldKey(field)) return;

    // 赤字の欄は、書き直している間も調べ直し、正しくなった時点で赤字を消す。
    // まだ赤字でない欄は、入力の途中で出すと邪魔なので、欄を離れるまで待つ
    setFieldErrors(prev => {
      const fields: FieldKey[] = [];
      if (field in prev) fields.push(field);
      // パスワードを直すと、確認欄の「一致しません」が解消することがある
      if (field === 'adminPassword' && 'adminPasswordConfirm' in prev) {
        fields.push('adminPasswordConfirm');
      }
      return fields.length > 0 ? revalidate(prev, fields, nextData) : prev;
    });
  };

  // 欄を離れたときに、その欄だけ調べる。登録ボタンを押す前に不備に気づけるように
  // （坂本様ご指摘②）。まだ何も入れていない欄は、通り過ぎただけなので赤字にしない
  const handleBlur = (field: FieldKey) => () => {
    if (!formData[field] && !(field in fieldErrors)) return;

    const fields: FieldKey[] = [field];
    // 確認欄を入れたあとでパスワードを直した場合に、食い違いを見逃さないように
    if (field === 'adminPassword' && formData.adminPasswordConfirm) {
      fields.push('adminPasswordConfirm');
    }
    setFieldErrors(prev => revalidate(prev, fields, formData));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsCompanyIdError(false);

    // 入力チェック。最初の1つで止めず、問題のある項目をすべて洗い出して
    // それぞれの入力欄の下に出す
    const errors = revalidate({}, FIELD_KEYS, formData);

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError(FIELD_ERROR_SUMMARY);
      return;
    }

    setIsLoading(true);

    try {
      // 会社の作成はサーバー側でまとめて行う。
      // companies を書くだけでは Firebase Auth のアカウントが作られず、
      // ログインできる状態にならないため。
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase/config');

      const register = httpsCallable<
        Record<string, string>,
        { companyId: string; freeTrialEndDate: string }
      >(functions, 'registerFreeTrialCompany');

      const { data } = await register({
        companyName: formData.companyName.trim(),
        corporateCode: formData.corporateCode.trim(),
        contactPerson: formData.contactPerson.trim(),
        contactEmail: formData.contactEmail.trim(),
        adminPassword: formData.adminPassword,
        industryType: formData.industryType,
        department: formData.department.trim(),
        phoneNumber: formData.phoneNumber.trim(),
      });

      // 完了画面は挟まず、そのまま管理画面へ入る。
      // 無料期間の終了日や会社IDは、管理画面の「登録情報のご案内」で
      // まとめてお伝えする。
      sessionStorage.setItem('registrationInfo', JSON.stringify({
        corporateCode: formData.corporateCode.trim(),
        contactEmail: formData.contactEmail.trim(),
        isNewRegistration: true,
        freeTrialEndDate: data.freeTrialEndDate,
      }));

      const { AuthService } = await import('../services/authService');
      await AuthService.signInWithEmailPassword(
        formData.corporateCode.trim(),
        formData.contactEmail.trim(),
        formData.adminPassword
      );

      // useAuthフックが管理者セッションを認識するまで待つ
      for (let i = 0; i < 20; i++) {
        const ls = typeof window !== 'undefined' ? window.localStorage : null;
        if (ls && ls.getItem('adminUserId')) {
          await new Promise(resolve => setTimeout(resolve, 300));
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      navigate('/admin', { replace: true });

    } catch (err) {
      console.error('[無料実証の会社登録] エラー:', err);
      // Firebase が返すメッセージは英語のため、そのまま出すと利用者に伝わらない。
      // 画面向けに用意した日本語のメッセージだけを表示する。
      const isJapanese = err instanceof Error && /[ぁ-んァ-ヶ一-龠]/.test(err.message);
      const message = isJapanese
        ? (err as Error).message
        : '登録処理に失敗しました。しばらく時間をおいてから再度お試しください。';
      const field = isJapanese ? fieldOfServerError(message) : undefined;
      if (field) {
        setFieldErrors({ [field]: message });
        setError(FIELD_ERROR_SUMMARY);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: '#ffffff', 
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar>
          <Typography 
            variant="h6" 
            sx={{ 
              color: AppColors.textPrimary,
              fontWeight: 600,
            }}
          >
            {/* 以前の表示: 選択プラン: {planName} */}
            会社情報を入力
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: AppColors.textPrimary,
              mb: 1,
            }}
          >
            会社情報を入力
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: AppColors.textSecondary,
            }}
          >
            {/* 以前の表示: 選択したプラン: {planName} */}
            ご利用いただく会社の情報を入力してください。
          </Typography>
        </Box>

        {/* 会社ID生成エラーの場合はダイアログ表示 */}
        <Dialog
          open={isCompanyIdError}
          onClose={() => {}}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle 
            id="alert-dialog-title"
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              color: AppColors.textPrimary,
            }}
          >
            会社ID生成エラー
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: AppColors.textPrimary,
                lineHeight: 1.6,
                mb: 1,
              }}
            >
              {error}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: AppColors.textSecondary,
                lineHeight: 1.5,
              }}
            >
会社IDを変更して、再度お試しください。
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setIsCompanyIdError(false)} 
              variant="contained"
              sx={{
                backgroundColor: AppColors.primary,
                '&:hover': {
                  backgroundColor: AppColors.primaryDark,
                },
              }}
            >
              閉じる
            </Button>
          </DialogActions>
        </Dialog>

        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ opacity: isCompanyIdError ? 0.5 : 1 }}>
          {/* 業種選択 */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: `1px solid ${AppColors.textSecondary}20`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: AppColors.textPrimary,
                mb: 2,
              }}
            >
              利用タイプを選択
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={formData.industryType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    industryType: e.target.value as IndustryType,
                  }))
                }
              >
                {(Object.entries(INDUSTRY_TYPE_LABELS) as [IndustryType, string][]).map(
                  ([value, label]) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio disabled={isLoading || isCompanyIdError} />}
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {label}
                        </Typography>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  )
                )}
              </RadioGroup>
            </FormControl>
            <Typography
              variant="caption"
              sx={{ color: AppColors.textSecondary, display: 'block', mt: 1 }}
            >
              ボックスのタブ名・説明文が業種に合わせた表示に切り替わります。
            </Typography>
          </Paper>

          {/* 会社名 */}
          <TextField
            fullWidth
            label="会社名"
            value={formData.companyName}
            onChange={handleInputChange('companyName')}
            onBlur={handleBlur('companyName')}
            required
            error={!!fieldErrors.companyName}
            helperText={fieldErrors.companyName}
            disabled={isLoading || isCompanyIdError}
            InputProps={{
              startAdornment: (
                <BusinessIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            // 会社ID・パスワードの条件文と同じく、エラーも左端にそろえる
            sx={{ mb: 3, '& .MuiFormHelperText-root': { mx: 0 } }}
          />

          {/* 会社ID */}
          <TextField
            fullWidth
            label="会社ID"
            value={formData.corporateCode}
            onChange={handleInputChange('corporateCode')}
            onBlur={handleBlur('corporateCode')}
            required
            error={!!fieldErrors.corporateCode}
            disabled={isLoading || isCompanyIdError}
            sx={{ 
              mb: 0.5,
              '& .MuiInputBase-input': {
                color: AppColors.textSecondary,
                backgroundColor: isLoading || isCompanyIdError ? AppColors.surface : 'inherit',
              },
            }}
            InputProps={{
              startAdornment: (
                <BadgeOutlined sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.25, mb: fieldErrors.corporateCode ? 0 : 3 }}>
            <Typography
              variant="caption"
              sx={{ color: AppColors.textSecondary }}
            >
              英数字と - _ のみ使用可。3〜32文字。重複不可。
            </Typography>
            <Button 
              onClick={generateCorporateCode}
              disabled={isLoading || isCompanyIdError}
              size="small"
              sx={{ color: AppColors.primary, alignSelf: 'flex-start', py: 0.25 }}
            >
              ランダムに生成
            </Button>
          </Box>
          {fieldErrors.corporateCode && (
            <Typography variant="caption" component="p" color="error" sx={{ mb: 3 }}>
              {fieldErrors.corporateCode}
            </Typography>
          )}

          {/* 担当者名 */}
          <TextField
            fullWidth
            label="担当者名"
            value={formData.contactPerson}
            onChange={handleInputChange('contactPerson')}
            onBlur={handleBlur('contactPerson')}
            required
            error={!!fieldErrors.contactPerson}
            helperText={fieldErrors.contactPerson}
            disabled={isLoading || isCompanyIdError}
            InputProps={{
              startAdornment: (
                <PersonIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            sx={{ mb: 3, '& .MuiFormHelperText-root': { mx: 0 } }}
          />

          {/* 部署名 */}
          <TextField
            fullWidth
            label="部署名（任意）"
            value={formData.department}
            onChange={handleInputChange('department')}
            disabled={isLoading || isCompanyIdError}
            InputProps={{
              startAdornment: (
                <BusinessCenterIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
          />

          {/* 連絡先メールアドレス */}
          <TextField
            fullWidth
            label="連絡先メールアドレス"
            type="email"
            value={formData.contactEmail}
            onChange={handleInputChange('contactEmail')}
            onBlur={handleBlur('contactEmail')}
            required
            error={!!fieldErrors.contactEmail}
            helperText={fieldErrors.contactEmail}
            disabled={isLoading || isCompanyIdError}
            InputProps={{
              startAdornment: (
                <EmailIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            sx={{ mb: 3, '& .MuiFormHelperText-root': { mx: 0 } }}
          />

          {/* 電話番号 */}
          <TextField
            fullWidth
            label="電話番号（任意）"
            value={formData.phoneNumber}
            onChange={handleInputChange('phoneNumber')}
            onBlur={handleBlur('phoneNumber')}
            error={!!fieldErrors.phoneNumber}
            helperText={fieldErrors.phoneNumber}
            disabled={isLoading || isCompanyIdError}
            InputProps={{
              startAdornment: (
                <PhoneIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            placeholder="例: 03-1234-5678"
            sx={{ mb: 3, '& .MuiFormHelperText-root': { mx: 0 } }}
          />

          {/* 管理者パスワード */}
          <FormControl fullWidth required error={!!fieldErrors.adminPassword} sx={{ mb: 0.5 }}>
            <InputLabel required>管理者パスワード</InputLabel>
             <OutlinedInput
              type={showPassword ? 'text' : 'password'}
              value={formData.adminPassword}
              onChange={handleInputChange('adminPassword')}
              onBlur={handleBlur('adminPassword')}
              required
              disabled={isLoading || isCompanyIdError}
              startAdornment={
                <InputAdornment position="start">
                  <LockIcon sx={{ color: AppColors.textSecondary }} />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <MuiIconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isLoading || isCompanyIdError}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </MuiIconButton>
                </InputAdornment>
              }
              label="管理者パスワード"
            />
          </FormControl>
          {/* 会社IDと同じく、入力する時点で条件が分かるようにする */}
          <Typography
            variant="caption"
            component="p"
            sx={{ color: AppColors.textSecondary, mt: 0.25, mb: fieldErrors.adminPassword ? 0 : 3 }}
          >
            {ADMIN_PASSWORD_RULE_LABEL}
          </Typography>
          {fieldErrors.adminPassword && (
            <Typography variant="caption" component="p" color="error" sx={{ mb: 3 }}>
              {fieldErrors.adminPassword}
            </Typography>
          )}

          {/* 管理者パスワード確認 */}
          <FormControl
            fullWidth
            required
            error={!!fieldErrors.adminPasswordConfirm}
            sx={{ mb: fieldErrors.adminPasswordConfirm ? 0.5 : 4 }}
          >
            <InputLabel required>管理者パスワード（確認）</InputLabel>
            <OutlinedInput
              type={showPasswordConfirm ? 'text' : 'password'}
              value={formData.adminPasswordConfirm}
              onChange={handleInputChange('adminPasswordConfirm')}
              onBlur={handleBlur('adminPasswordConfirm')}
              required
              disabled={isLoading || isCompanyIdError}
              startAdornment={
                <InputAdornment position="start">
                  <LockIcon sx={{ color: AppColors.textSecondary }} />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <MuiIconButton
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    edge="end"
                    disabled={isLoading || isCompanyIdError}
                  >
                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </MuiIconButton>
                </InputAdornment>
              }
              label="管理者パスワード（確認）"
            />
          </FormControl>
          {fieldErrors.adminPasswordConfirm && (
            <Typography variant="caption" component="p" color="error" sx={{ mb: 4 }}>
              {fieldErrors.adminPasswordConfirm}
            </Typography>
          )}

          {/* 登録ボタン */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || isCompanyIdError}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: AppColors.primary,
              '&:hover': {
                backgroundColor: AppColors.primaryDark,
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              '会社を登録する'
            )}
          </Button>

          {/* その他のエラーはボタンのすぐ下に出す。
              画面上部に出すと、ボタンを押しても手元では何も変わらず気づけないため */}
          {error && !isCompanyIdError && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* 注意事項 */}
        <Box
            sx={{
              mt: 4,
              p: 3,
              backgroundColor: AppColors.surface,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: AppColors.textSecondary,
                lineHeight: 1.6,
              }}
            >
              <strong>注意事項:</strong><br />
              • ご登録後、そのまま無料でのご利用を開始いただけます<br />
              • 無料期間はご登録から30日以上です。終了日は管理画面に表示されます<br />
              • 無料期間中にお支払い情報をご登録いただく必要はありません<br />
              • 継続される場合は、管理画面からお支払い情報をご登録ください<br />
              • 入力された情報は安全に保存されます<br />
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
